// QueryTool.js
import React, { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import axios from "../axiosConfig";
// Importing components
import SearchBox from "../components/General/SearchBox";
import ActionButtons from "../components/QueryTool/ActionButtons";
import TableHeader from "../components/QueryTool/TableHeader";
import AddNewRow from "../components/QueryTool/AddNewRow";
import DataRows from "../components/QueryTool/DataRows";
import SynonymGenerator from "../components/QueryTool/SynonymGenerator";

function QueryTool({ accessToken }) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [activeSearchTerms, setActiveSearchTerms] = useState([]);

  // Parse search query into groups with AND/OR logic
  const parseSearchQuery = (query) => {
    if (!query || typeof query !== "string") return { groups: [], allTerms: [] };
    
    // First split by OR operators (comma, slash, space)
    // We need to preserve & groups, so we'll do this carefully
    const orParts = query.split(/[,\/]/).map(part => part.trim()).filter(p => p);
    
    const groups = [];
    const allTerms = [];
    
    orParts.forEach(part => {
      // Check if this part contains AND operator (&)
      if (part.includes('&')) {
        const andTerms = part
          .split('&')
          .map(t => t.trim())
          .filter(t => t);
        
        if (andTerms.length > 0) {
          groups.push({ type: 'AND', terms: andTerms });
          allTerms.push(...andTerms);
        }
      } else {
        // Split by space for individual OR terms
        const spaceTerms = part
          .split(/\s+/)
          .map(t => t.trim())
          .filter(t => t);
        
        spaceTerms.forEach(term => {
          groups.push({ type: 'OR', terms: [term] });
          allTerms.push(term);
        });
      }
    });
    
    // Remove duplicate terms for highlighting
    const uniqueTerms = [...new Set(allTerms)];
    
    return { groups, allTerms: uniqueTerms };
  };

  // Check if a row matches the search criteria
  const rowMatchesSearch = (row, searchGroups) => {
    if (!searchGroups || searchGroups.length === 0) return true;
    
    // Convert all row values to lowercase string for searching
    const rowText = Object.values(row)
      .map(val => String(val ?? "").toLowerCase())
      .join(" ");
    
    // Check if any group matches (OR logic between groups)
    return searchGroups.some(group => {
      if (group.type === 'AND') {
        // All terms in this group must be present
        return group.terms.every(term => 
          rowText.includes(term.toLowerCase())
        );
      } else {
        // At least one term must be present (OR)
        return group.terms.some(term => 
          rowText.includes(term.toLowerCase())
        );
      }
    });
  };

  const loadWorkbook = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await axios.get("/get-file", {
        headers: { Authorization: `Bearer ${accessToken}` },
        responseType: "arraybuffer",
        validateStatus: (s) => (s >= 200 && s < 300) || s === 204,
      });
      if (res.status === 204 || !res.data || res.data.byteLength === 0) {
        setData([]);
        setFilteredData([]);
        return;
      }
      const workbook = XLSX.read(res.data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
      setData(jsonData);
      setFilteredData(jsonData);
    } catch (err) {
      console.error("Error fetching Excel from /get-file:", err);
      setData([]);
      setFilteredData([]);
    }
  }, [accessToken]);

  useEffect(() => {
    loadWorkbook();
  }, [loadWorkbook]);

  // Filter the data based on the search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredData(data);
      setActiveSearchTerms([]);
    } else {
      const { groups, allTerms } = parseSearchQuery(searchQuery);
      
      const filtered = data.filter((row) => rowMatchesSearch(row, groups));
      
      setFilteredData(filtered);
      setActiveSearchTerms(allTerms);
    }
  }, [searchQuery, data]);

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const handleCellEdit = (rowIndex, field, newValue) => {
    setHistory((prev) => [
      ...prev,
      {
        data: JSON.parse(JSON.stringify(data)),
        filteredData: JSON.parse(JSON.stringify(filteredData)),
      },
    ]);
    setRedoStack([]);
    const updatedFiltered = [...filteredData];
    updatedFiltered[rowIndex] = { ...updatedFiltered[rowIndex], [field]: newValue };
    setFilteredData(updatedFiltered);
    const actualRowIndex = data.findIndex((row) => row === filteredData[rowIndex]);
    if (actualRowIndex !== -1) {
      const updated = [...data];
      updated[actualRowIndex] = { ...updated[actualRowIndex], [field]: newValue };
      setData(updated);
    }
    setUnsavedChanges(true);
  };

  const handleDeleteRow = (rowIndex) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this row? Once saved, it can't be recovered!"
    );
    if (!confirmDelete) return;
    setHistory((prev) => [
      ...prev,
      {
        data: JSON.parse(JSON.stringify(data)),
        filteredData: JSON.parse(JSON.stringify(filteredData)),
      },
    ]);
    setRedoStack([]);
    const rowToDelete = filteredData[rowIndex];
    const actualRowIndex = data.findIndex((row) => row === rowToDelete);
    if (actualRowIndex !== -1) {
      setFilteredData(filteredData.filter((_, i) => i !== rowIndex));
      setData(data.filter((_, i) => i !== actualRowIndex));
      setUnsavedChanges(true);
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const lastState = history[history.length - 1];
    setRedoStack((prev) => [
      {
        data: JSON.parse(JSON.stringify(data)),
        filteredData: JSON.parse(JSON.stringify(filteredData)),
      },
      ...prev,
    ]);
    setData(lastState.data);
    setFilteredData(lastState.filteredData);
    setHistory(history.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const nextState = redoStack[0];
    setHistory((prev) => [
      {
        data: JSON.parse(JSON.stringify(data)),
        filteredData: JSON.parse(JSON.stringify(filteredData)),
      },
      ...prev,
    ]);
    setData(nextState.data);
    setFilteredData(nextState.filteredData);
    setRedoStack(redoStack.slice(1));
  };

  const handleAddRow = () => {
    const newRowData = Object.keys(data[0] || {}).reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {});
    setNewRow(newRowData);
  };

  const handleSaveNewRow = () => {
    setHistory((prev) => [
      ...prev,
      {
        data: JSON.parse(JSON.stringify(data)),
        filteredData: JSON.parse(JSON.stringify(filteredData)),
      },
    ]);
    setRedoStack([]);
    setData((prev) => [...prev, newRow]);
    setFilteredData((prev) => [...prev, newRow]);
    setNewRow({});
    setUnsavedChanges(true);
  };

  const handleCancelNewRow = () => setNewRow({});

  return (
    <div className="flex h-screen bg-white">
      <div
        className="w-1/5 p-4 bg-gray-300 shadow-[#7e7e7e] shadow-lg text-center"
        style={{ position: "sticky", top: 0 }}
      >
        <h1 className="text-lg font-bold mb-4">Query String Search Tool</h1>
        <div className="bg-white shadow-md p-3 rounded-xl">
          <SearchBox searchQuery={searchQuery} handleSearch={handleSearch} mode={1} />
          <ActionButtons
            undoStack={history}
            handleUndo={handleUndo}
            redoStack={redoStack}
            handleRedo={handleRedo}
            data={data}
            setUnsavedChanges={setUnsavedChanges}
            unsavedChanges={unsavedChanges}
            handleAddRow={handleAddRow}
            accessToken={accessToken}
            onAfterSaveOrUpload={loadWorkbook}
          />
        </div>
        <SynonymGenerator />
      </div>
      <div className="w-4/5 p-4 word-wrap-auto bg-white" style={{ overflowY: "auto" }}>
        <table className="min-w-full rounded-xl shadow-[#cccccc] shadow-xl text-surface border-separate border-2 border-gray-400">
          <TableHeader data_0={data[0]} />
          <tbody className="rounded-xl text-sm">
            {Object.keys(newRow).length > 0 && (
              <AddNewRow
                newRow={newRow}
                setNewRow={setNewRow}
                handleSaveNewRow={handleSaveNewRow}
                handleCancelNewRow={handleCancelNewRow}
              />
            )}
            {filteredData.map((row, rowIndex) => (
              <DataRows
                key={rowIndex}
                row={row}
                rowIndex={rowIndex}
                handleCellEdit={handleCellEdit}
                handleDeleteRow={handleDeleteRow}
                searchTerms={activeSearchTerms}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default QueryTool;